import { Candidate } from "@type/candidate";

export const anonymizeName = (name: string): string => {
  if (!name) {
    return "";
  }

  const parts = name.split(" ");
  const initials = parts.map((part) => part.charAt(0).toUpperCase() + ".");
  return initials.join(" ");
};

export const getName = (candidate: Candidate): string => {
  if (candidate.user.first_name)
    return anonymizeName(
      candidate.user.first_name + " " + candidate.user.last_name
    );
  if (candidate.cv_data.CV_data.name)
    return anonymizeName(candidate.cv_data.CV_data.name);
  else return "N. N.";
};

/**
 * Extracts the first name(s) from a full name string.
 * The first name is considered to be all parts of the name
 * that appear before the last part when split by spaces.
 * Handles multiple spaces between names and leading/trailing spaces.
 *
 * @param fullName The full name string.
 * @returns The first name(s) as a string. Returns the original string if
 * it contains only one part or is empty after trimming.
 */
export function getFirstName(fullName: string): string {
  // 1. Trim leading/trailing whitespace
  if (!fullName) {
    return ""; // Handle null or undefined case
  }

  const trimmedName = fullName.trim();

  // 2. Handle empty string case
  if (trimmedName === "") {
    return "";
  }

  // 3. Split the name into parts using one or more spaces as delimiter
  //    This handles cases like "John   Doe" correctly.
  const parts = trimmedName.split(/\s+/);

  // 4. If there's only one part (or fewer, though split usually yields at least one),
  //    it's considered the entire name, so return it as is.
  if (parts.length <= 1) {
    return trimmedName; // Return the original trimmed name
  }

  // 5. Get all parts except the last one
  const firstNameParts = parts.slice(0, parts.length - 1);

  // 6. Join the first name parts back together with a single space
  const firstName = firstNameParts.join(" ");

  return firstName;
}

export const hideEmailUsername = (email: string): string => {
  if (!email || !email.includes("@")) {
    return email;
  }

  const [username, domain] = email.split("@");
  const hiddenUsername =
    username.charAt(0) +
    "*".repeat(Math.max(0, username.length - 2)) +
    username.charAt(username.length - 1);

  return hiddenUsername + "@" + domain;
};

export const hidePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber || phoneNumber.length < 4) {
    return phoneNumber;
  }

  const visibleDigits = phoneNumber.slice(-4);
  const hiddenPart = "*".repeat(phoneNumber.length - 4);

  return hiddenPart + visibleDigits;
};

// Censor part of the name and email
export const censorName = (name: string) => {
  if (!name || name.length <= 3) return name;
  const firstPart = name.slice(0, Math.ceil(name.length / 3));
  const lastPart = name.slice(-2);
  const middlePart = "*".repeat(
    Math.min(4, name.length - firstPart.length - lastPart.length)
  );
  return `${firstPart}${middlePart}${lastPart}`;
};

export const getSubdomainHost = (subdomain: string) => {
  if (import.meta.env.VITE_APP_ENV === "development") {
    return import.meta.env.VITE_TALENT_AI_APP;
  }
  if (!subdomain) return import.meta.env.VITE_TALENT_AI_APP;
  return `${window.location.protocol}//${subdomain}.${import.meta.env.VITE_TALENT_MAIN_DOMAIN}`;
}